import { createTheme, ThemeOptions } from '@mui/material/styles';

// Custom color palette for futuristic dark theme
export const futuristicColors = {
  primary: {
    main: '#00D4FF',
    light: '#33DDFF',
    dark: '#0099CC',
    contrastText: '#000000',
  },
  secondary: {
    main: '#FF00FF',
    light: '#FF33FF',
    dark: '#CC00CC',
    contrastText: '#FFFFFF',
  },
  background: {
    default: '#0A0A0A',
    paper: '#1A1A1A',
    gradient: 'linear-gradient(135deg, #0A0A0A 0%, #1A1A1A 100%)',
  },
  text: {
    primary: '#FFFFFF',
    secondary: '#B0B0B0',
    disabled: '#666666',
  },
  divider: 'rgba(255, 255, 255, 0.1)',
  action: {
    active: '#00D4FF',
    hover: 'rgba(0, 212, 255, 0.1)',
    selected: 'rgba(0, 212, 255, 0.2)',
    disabled: 'rgba(255, 255, 255, 0.3)',
    disabledBackground: 'rgba(255, 255, 255, 0.12)',
  },
  neon: {
    cyan: '#00D4FF',
    magenta: '#FF00FF',
    green: '#00FF88',
    yellow: '#FFD700',
    orange: '#FF6B35',
  },
};

// Custom typography for futuristic feel
export const futuristicTypography = {
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  h1: {
    fontFamily: '"Orbitron", "Inter", sans-serif',
    fontWeight: 700,
    fontSize: '3.5rem',
    letterSpacing: '0.02em',
    textShadow: '0 0 20px rgba(0, 212, 255, 0.5)',
  },
  h2: {
    fontFamily: '"Orbitron", "Inter", sans-serif',
    fontWeight: 600,
    fontSize: '2.5rem',
    letterSpacing: '0.01em',
    textShadow: '0 0 15px rgba(255, 0, 255, 0.3)',
  },
  h3: {
    fontFamily: '"Orbitron", "Inter", sans-serif',
    fontWeight: 600,
    fontSize: '2rem',
  },
  h4: {
    fontFamily: '"Orbitron", "Inter", sans-serif',
    fontWeight: 500,
    fontSize: '1.5rem',
  },
  h5: {
    fontFamily: '"Orbitron", "Inter", sans-serif',
    fontWeight: 500,
    fontSize: '1.25rem',
  },
  h6: {
    fontFamily: '"Orbitron", "Inter", sans-serif',
    fontWeight: 500,
    fontSize: '1rem',
  },
  body1: {
    fontSize: '1rem',
    lineHeight: 1.6,
  },
  body2: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
  },
  button: {
    fontFamily: '"Orbitron", "Inter", sans-serif',
    fontWeight: 600,
    letterSpacing: '0.05em',
    textTransform: 'uppercase' as const,
  },
};

// Component overrides for futuristic styling
const componentOverrides: ThemeOptions['components'] = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: '8px',
        padding: '12px 24px',
        fontWeight: 600,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        background: 'linear-gradient(135deg, #00D4FF 0%, #FF00FF 100%)',
        border: '1px solid rgba(0, 212, 255, 0.3)',
        boxShadow: '0 4px 20px rgba(0, 212, 255, 0.3)',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 30px rgba(0, 212, 255, 0.5)',
          background: 'linear-gradient(135deg, #33DDFF 0%, #FF33FF 100%)',
        },
        '&:active': {
          transform: 'translateY(0)',
        },
        '&.Mui-disabled': {
          background: 'rgba(255, 255, 255, 0.12)',
          color: 'rgba(255, 255, 255, 0.3)',
          boxShadow: 'none',
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        background: 'linear-gradient(135deg, #1A1A1A 0%, #2A2A2A 100%)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 40px rgba(0, 212, 255, 0.2)',
          border: '1px solid rgba(0, 212, 255, 0.3)',
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: '20px',
        fontWeight: 600,
        letterSpacing: '0.02em',
        background: 'rgba(0, 212, 255, 0.1)',
        border: '1px solid rgba(0, 212, 255, 0.3)',
        color: '#00D4FF',
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: '12px',
          background: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          transition: 'all 0.3s ease',
          '&:hover': {
            border: '1px solid rgba(0, 212, 255, 0.3)',
            background: 'rgba(255, 255, 255, 0.08)',
          },
          '&.Mui-focused': {
            border: '1px solid #00D4FF',
            boxShadow: '0 0 20px rgba(0, 212, 255, 0.3)',
            background: 'rgba(255, 255, 255, 0.1)',
          },
        },
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        background: 'rgba(10, 10, 10, 0.8)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      },
    },
  },
};

// Create the futuristic theme
export const futuristicTheme = createTheme({
  palette: futuristicColors,
  typography: futuristicTypography,
  components: componentOverrides,
  shape: {
    borderRadius: 12,
  },
  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
  },
});

export default futuristicTheme;